import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, onSnapshot, doc, deleteDoc, getDoc } from "firebase/firestore";
import "./Screens.css";
import AddEditScreenModal from "../components/AddEditScreenModal";

const Screens = () => {
  const [screens, setScreens] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const screensCollection = collection(db, "screens");
    const screensQuery = query(screensCollection);

    const unsubscribe = onSnapshot(screensQuery, async (snapshot) => {
      const now = new Date();

      const screensData = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const lastActivity = data.lastActivity ? data.lastActivity.toDate() : null;
        const isOnline = lastActivity && (now - lastActivity < 40000); // 40s threshold

        // ✅ Ensure `nowPlaying` is synced with player
        let nowPlaying = data.nowPlaying || null;
        if (data.playerId) {
          const playerDocRef = doc(db, "players", data.playerId);
          const playerSnapshot = await getDoc(playerDocRef);
          if (playerSnapshot.exists()) {
            const playerData = playerSnapshot.data();
            nowPlaying = playerData.nowPlaying || null;
          }
        }

        return {
          id: docSnap.id,
          ...data,
          status: isOnline ? "Online" : "Offline",
          nowPlaying,
        };
      }));

      setScreens(screensData);
    });

    return () => unsubscribe();
  }, []);

  const handleAddScreen = () => {
    setSelectedScreen(null);
    setIsModalOpen(true);
  };

  const handleEditScreen = (screen) => {
    setSelectedScreen(screen);
    setIsModalOpen(true);
  };

  const handleDeleteScreen = async (screenId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this screen?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "screens", screenId));
      setScreens((prevScreens) => prevScreens.filter((screen) => screen.id !== screenId));
    } catch (error) {
      console.error("Error deleting screen:", error.message);
      alert("Failed to delete screen. Please try again.");
    }
  };

  // ✅ Filters screens based on search query
  const filteredScreens = screens.filter((screen) =>
    screen.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="screens-container">
      <div className="screens-header">
        <h1 className="screens-title">Screens</h1>
        <button className="btn add-screen" onClick={handleAddScreen}>
          Add Screen
        </button>
      </div>

      <div className="search-bar-container left-aligned">
        <input
          type="text"
          className="search-bar"
          placeholder="Search screens..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="screens-table-container">
        <table className="screens-table">
          <thead>
            <tr>
              <th>Screen Name</th>
              <th>Status</th>
              <th>Player ID</th>
              <th>Last Activity</th>
              <th>Now Playing</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredScreens.map((screen) => (
              <tr key={screen.id}>
                <td>{screen.name}</td>
                <td>
                  <span className={`status-badge ${screen.status === "Online" ? "online" : "offline"}`}>
                    {screen.status}
                  </span>
                </td>
                <td>{screen.playerId || "N/A"}</td>
                <td>{screen.lastActivity ? new Date(screen.lastActivity.seconds * 1000).toLocaleTimeString() : "Unknown"}</td>
                <td>
                  {screen.nowPlaying ? (
                    <a href={`/content/${screen.nowPlaying.id}`} className="now-playing-link">
                      {screen.nowPlaying.name}
                    </a>
                  ) : (
                    "No content"
                  )}
                </td>
                <td>
                  <button className="btn edit" onClick={() => handleEditScreen(screen)}>Edit</button>
                  <button className="btn delete" onClick={() => handleDeleteScreen(screen.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddEditScreenModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} existingScreen={selectedScreen} />
    </div>
  );
};

export default Screens;
